import React from 'react';
import { Row, Col } from 'react-bootstrap';
import './reports.css';

const floatingLabelStyle = {
    position: 'absolute',
    top: 0,
    left: 8,
    background: '#fff',
    padding: '0 4px',
    fontSize: 12,
    color: '#888',
    zIndex: 2
};

const hasLabelNode = (node) => {
    if (!React.isValidElement(node)) {
        return false;
    }

    const nodeType = node.type;
    const isLabelNode = nodeType === 'label' || nodeType?.displayName === 'Label' || nodeType?.name === 'Label';

    if (isLabelNode) {
        return true;
    }

    const nestedChildren = React.Children.toArray(node.props?.children || []);
    return nestedChildren.some((child) => hasLabelNode(child));
};

const normalizePlaceholderLabel = (placeholder = '') => {
    if (typeof placeholder !== 'string') {
        return null;
    }

    const trimmed = placeholder.trim();
    if (!trimmed) {
        return null;
    }

    if (/filter\s*by/i.test(trimmed) || /^none\b/i.test(trimmed)) {
        return 'Filter By';
    }

    const withoutSelectPrefix = trimmed.replace(/^select\s+/i, '');
    const withoutCount = withoutSelectPrefix.replace(/\s*\(\d+\)\s*$/, '').trim();

    if (/^date\s*range$/i.test(withoutCount)) {
        return 'Date Range';
    }

    return withoutCount || null;
};

const resolveChildLabel = (child) => {
    if (!React.isValidElement(child)) {
        return null;
    }

    if (hasLabelNode(child)) {
        return null;
    }

    const componentName = child.type?.displayName || child.type?.name;
    if (componentName === 'DateRange') {
        if (typeof child.props?.label === 'string' && child.props.label.trim()) {
            return child.props.label.trim();
        }
        return 'Date Range';
    }

    return normalizePlaceholderLabel(child.props?.placeholder);
};

const hasSelectedValue = (value) => {
    if (Array.isArray(value)) {
        return value.length > 0;
    }

    if (value && typeof value === 'object') {
        return Object.keys(value).length > 0;
    }

    return value !== null && value !== undefined && String(value).trim() !== '';
};

const isChildFilled = (child) => {
    if (!React.isValidElement(child)) {
        return false;
    }

    const componentName = child.type?.displayName || child.type?.name;
    if (componentName === 'DateRange') {
        const range = child.props?.dateRange?.[0];
        return Boolean(range?.startDate || range?.endDate);
    }

    const value = child.props?.value ?? child.props?.defaultValue;
    return hasSelectedValue(value);
};

const withAutoLabel = (child) => {
    const label = resolveChildLabel(child);
    const isFilled = isChildFilled(child);

    if (!label) {
        return child;
    }

    return (
        <div className={`report-filter-control ${isFilled ? 'is-filled' : ''}`}>
            <label className="select-floating-label report-filter-label mb-1" style={floatingLabelStyle}>{label}</label>
            {child}
        </div>
    );
};

const FiltersRow = ({ children = [], className = '' }) => {
    const safeChildren = React.Children.toArray(children).filter((child) => child !== null && child !== undefined);

    return (
        <Row className={`mx-n2 d-flex flex-wrap justify-content-end mt-2 ${className}`}>
            {safeChildren.map((child, index) => (
                <Col 
                    key={index} 
                    className="px-2 mt-3" 
                    style={{ flex: '1 1 240px', maxWidth: '240px', justifyContent: 'flex-end', flexDirection:"column", display: 'flex' }}
                >
                    {withAutoLabel(child)}
                </Col>
            ))}
        </Row>
    );
};

export default FiltersRow;